'use client';

import { useState, useEffect } from 'react';

import '@/styles/vendors/image-zoom.css';
import Zoom from 'react-medium-image-zoom';

interface Resolution {
  width: number;
  height: number;
}

interface ImageResolutions {
  original: Resolution;
  medium: Resolution;
  large: Resolution;
  thumbnail: Resolution;
  medium_large: Resolution;
  '1536x1536': Resolution;
}

interface ImageData {
  id: number;
  resolution: ImageResolutions;
  original: string;
  medium: string;
  large: string;
  thumbnail: string;
  medium_large: string;
  '1536x1536': string;
  srcset: string;
  child?: ImageData;
}

interface ResponsiveImageProps {
  alt?: string;
  imageData: ImageData;
  auto?: boolean;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
  zoom?: boolean;
}

export const ResponsiveImage = ({
  alt = undefined,
  imageData,
  auto = true,
  className = '',
  onLoad,
  onError,
  zoom = false,
}: ResponsiveImageProps) => {
  const [selectedSrc, setSelectedSrc] = useState<string>('');

  useEffect(() => {
    if (!imageData) return;

    if (!auto) {
      setSelectedSrc(imageData.medium || imageData.original || '');
      return;
    }

    const containerWidth = window.innerWidth;
    const availableSizes = Object.entries({
      original: 1600, // 1600x1200
      '1536x1536': 1536, // 1536x1152
      large: 800, // 800x600
      medium_large: 768, // 768x576
      medium: 533, // 533x400
      thumbnail: 300, // 300x225
    }).filter(([key]) => imageData[key]);

    const bestSize = availableSizes.find(([_, width]) => containerWidth >= width)?.[0] || 'medium';
    setSelectedSrc(imageData[bestSize] || imageData.original || '');
  }, [imageData, auto]);

  if (!imageData || !selectedSrc) return null;

  const pictureElement = (
    <picture className={className}>
      {imageData.original && <source media="(min-width: 1600px)" srcSet={imageData.original} />}
      {imageData['1536x1536'] && <source media="(min-width: 1536px)" srcSet={imageData['1536x1536']} />}
      {imageData.large && <source media="(min-width: 800px)" srcSet={imageData.large} />}
      {imageData.medium_large && <source media="(min-width: 768px)" srcSet={imageData.medium_large} />}
      {imageData.medium && <source media="(min-width: 533px)" srcSet={imageData.medium} />}
      {imageData.thumbnail && <source media="(max-width: 300px)" srcSet={imageData.thumbnail} />}
      <img
        src={selectedSrc}
        alt={alt}
        className={`w-full h-auto object-cover ${className}`}
        loading="lazy"
        srcSet={imageData.srcset}
        sizes="(max-width: 1600px) 100vw, 1600px"
        onLoad={onLoad}
        onError={onError}
      />
    </picture>
  );

  return zoom ? <Zoom>{pictureElement}</Zoom> : pictureElement;
};

export default ResponsiveImage;
